/** Trends (Visitors Desk): the long line's chart props, the scorecard wording and the reader ladder by month.
 *  GA4 is context only: it keeps its own `GA4` / `GA4 last year` series and label and is never blended into first-party values. */
import type { TrendSeriesLine } from '../components/charts/TrendChart'
import type { Compared, LadderMonth, ScoreRow, TrendMetric, TrendSeries, Verdict } from '../api/visitorsDesk'
import { changeText } from './deskFormat'
import { fmtInt } from './format'
import { markersFrom } from './deskAlerts'

const DASH = '—'

/** Metric labels, as the API names them (aamcirc/visitors/desk/trends.py TREND_METRICS). */
export const TREND_METRICS: { value: TrendMetric; label: string }[] = [
  { value: 'people', label: 'People' },
  { value: 'regular', label: 'Regular readers (5+ days)' },
  { value: 'known_new', label: 'Known (new sign-ups)' },
  { value: 'members_gained', label: 'Members gained' },
  { value: 'members_active', label: 'Members active' },
  { value: 'google_clicks', label: 'Google search clicks' },
  { value: 'social_visits', label: 'Social visits' },
  { value: 'email_visits', label: 'Email visits' },
  { value: 'issue_opens', label: 'Issue opens' },
  { value: 'bot_share', label: 'Bot share of raw traffic' },
]

export function isTrendMetric(v: string | null | undefined): v is TrendMetric {
  return TREND_METRICS.some((m) => m.value === v)
}

export const GA4_LABEL = 'GA4'
export const GA4_LAST_YEAR_LABEL = 'GA4 last year'

export interface TrendChartProps {
  series: TrendSeriesLine[]
  labels: string[]
  band?: { low: (number | null)[]; high: (number | null)[] }
  incompleteFrom?: number
  markers: { index: number; label: string }[]
}

function daysBetween(start: string, end: string): string[] {
  const out: string[] = []
  const d = new Date(`${start}T12:00:00Z`)
  const stop = new Date(`${end}T12:00:00Z`).getTime()
  if (Number.isNaN(d.getTime()) || Number.isNaN(stop)) return out
  while (d.getTime() <= stop && out.length < 2000) {
    out.push(d.toISOString().slice(0, 10))
    d.setUTCDate(d.getUTCDate() + 1)
  }
  return out
}

/** Builds the TrendChart props: 28-day average (emphasis), Daily, Last year or GA4 last year (dashed),
 *  and GA4 history as its own dashed series. The band is the normal range; the dotted tail starts at the
 *  first incomplete point. */
export function seriesForChart(s: TrendSeries): TrendChartProps {
  const labels = daysBetween(s.start, s.end)
  for (const p of s.points) if (!labels.includes(p.day)) labels.push(p.day)
  labels.sort()
  const at = new Map(labels.map((d, i) => [d, i]))
  const fill = (rows: { day: string; value: number | null }[]) => {
    const v: (number | null)[] = labels.map(() => null)
    for (const r of rows) {
      const i = at.get(r.day)
      if (i !== undefined) v[i] = r.value
    }
    return v
  }
  const has = (v: (number | null)[]) => v.some((x) => x !== null)
  const byDay = new Map(s.points.map((p) => [p.day, p]))
  const col = (f: (p: TrendSeries['points'][number]) => number | null) => labels.map((d) => { const p = byDay.get(d); return p ? f(p) : null })

  const series: TrendSeriesLine[] = [
    { label: '28-day average', values: col((p) => p.rolling28), emphasis: true },
    { label: 'Daily', values: col((p) => p.value) },
  ]
  const ly = fill(s.last_year)
  if (has(ly)) series.push({ label: 'Last year', values: ly, dashed: true })
  else if (s.ga4_last_year) {
    const g = fill(s.ga4_last_year.points)
    if (has(g)) series.push({ label: GA4_LAST_YEAR_LABEL, values: g, dashed: true })
  }
  if (s.ga4_history) {
    const g = fill(s.ga4_history.points)
    if (has(g)) series.push({ label: GA4_LABEL, values: g, dashed: true })
  }
  const low = col((p) => p.low)
  const high = col((p) => p.high)
  const firstIncomplete = labels.findIndex((d) => byDay.get(d)?.incomplete)
  return {
    series,
    labels,
    band: has(low) && has(high) ? { low, high } : undefined,
    incompleteFrom: firstIncomplete >= 0 ? firstIncomplete : undefined,
    markers: markersFrom(s.annotations, labels),
  }
}

const VERDICT_WORD: Record<Verdict, string> = { normal: 'Normal', above: 'Above', below: 'Below', collecting: 'Collecting' }

export function scoreVerdictText(v: Verdict | null | undefined): string {
  return v ? VERDICT_WORD[v] : DASH
}

export const pctValue = (v: number) => `${Math.round(v * 1000) / 10}%`

/** A scorecard value: counts with separators, rates as percentages, null as a dash. */
export function scoreValue(kind: 'count' | 'rate', v: number | null | undefined): string {
  if (v === null || v === undefined) return DASH
  return kind === 'rate' ? `${Math.round(v * 100)}%` : fmtInt(Math.round(v))
}

/** The change cell: percent for counts, points for rates, a dash when absent. */
export function scoreChange(kind: 'count' | 'rate', change: number | null, pct: number | null) {
  return changeText({ change, change_pct: pct, points: kind === 'rate' } as Compared)
}

/** Last year's value with its source when it is not first-party (`4,980 (GA4 last year)`). */
export function lastYearCell(r: Pick<ScoreRow, 'kind' | 'last_year' | 'last_year_label'>): string {
  // No first-party last year yet: the server may send GA4 context as text ("GA4: 812 a day").
  if (r.last_year === null || r.last_year === undefined) return r.last_year_label?.startsWith('GA4') ? r.last_year_label : DASH
  const v = scoreValue(r.kind, r.last_year)
  return r.last_year_label && r.last_year_label !== 'Last year' ? `${v} (${r.last_year_label})` : v
}

export const LADDER_COLUMNS: { key: string; label: string }[] = [
  { key: 'occasional', label: 'Occasional' },
  { key: 'regular', label: 'Regular' },
  { key: 'known', label: 'Known' },
  { key: 'member', label: 'Members' },
  { key: 'engaged', label: 'Engaged' },
]

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `Oct 2026 (to date)`; the newest first, as the mockup lists it. */
export function ladderRows(rows: LadderMonth[]): { month: string; cells: string[] }[] {
  return [...rows].reverse().map((r) => {
    const [y, m] = r.month.split('-')
    const name = `${MONTHS[Number(m) - 1] ?? m} ${y}${r.to_date ? ' (to date)' : ''}`
    return { month: name, cells: LADDER_COLUMNS.map((c) => { const v = r.people[c.key]; return v === null || v === undefined ? DASH : fmtInt(v) }) }
  })
}
